/**
 * HeyGen metadata API client (read-only).
 *
 * Используется только для UI-picker'ов (avatar list, voice list, languages).
 * Сама генерация видео идёт через стандартный `/api/v1/video/generate`
 * (HeyGen-specific поля передаются прямо в VideoGenerateRequest).
 *
 * Backend endpoint реализует гибридную логику: пытается live-fetch с
 * heygen.com, при неудаче возвращает hardcoded fallback. Поле `source`
 * в response показывает что отдали — `live`, `cache` или `fallback`.
 */
import api from './client'

// ── Types ────────────────────────────────────────────────────────

export interface HeyGenAvatar {
	avatar_id: string
	avatar_name: string
	gender: 'male' | 'female' | 'unknown'
	preview_image_url: string | null
	preview_video_url: string | null
	premium?: boolean
	type?: string // public | talking_photo | custom
	tags?: string[]
	default_voice_id?: string | null
}

export interface HeyGenVoice {
	voice_id: string
	name: string
	language: string
	locale?: string
	gender: 'male' | 'female' | 'unknown'
	preview_audio: string | null
	support_pause?: boolean
	emotion_support?: boolean
	support_interactive_avatar?: boolean
}

export interface HeyGenLanguage {
	code: string
	label: string
}

export interface HeyGenConstants {
	emotions: string[]
	avatar_styles: Array<'normal' | 'closeUp' | 'circle'>
	orientations: Array<{ code: 'landscape' | 'portrait'; label: string }>
	voice_speed: { min: number; max: number; default: number; step: number }
	translate_modes: Array<{ code: string; label: string }>
}

interface ListResponse<T, K extends string> {
	ok: boolean
	total: number
	source: 'live' | 'cache' | 'fallback'
}

type AvatarsResponse = ListResponse<HeyGenAvatar, 'avatars'> & { avatars: HeyGenAvatar[] }
type VoicesResponse = ListResponse<HeyGenVoice, 'voices'> & { voices: HeyGenVoice[] }
type LanguagesResponse = ListResponse<HeyGenLanguage, 'languages'> & { languages: HeyGenLanguage[] }
type ConstantsResponse = HeyGenConstants & { ok: boolean }

// ── API ──────────────────────────────────────────────────────────

export async function listHeyGenAvatars(filters?: {
	gender?: 'male' | 'female'
	search?: string
}): Promise<HeyGenAvatar[]> {
	const params = new URLSearchParams()
	if (filters?.gender) params.set('gender', filters.gender)
	if (filters?.search) params.set('search', filters.search)
	const qs = params.toString()
	const res = await api.request<AvatarsResponse>(
		`/api/v1/heygen/avatars${qs ? `?${qs}` : ''}`,
		{ method: 'GET' },
	)
	return res.avatars || []
}

export async function listHeyGenVoices(filters?: {
	language?: string
	gender?: 'male' | 'female' | 'unknown'
	emotion_only?: boolean
	search?: string
}): Promise<HeyGenVoice[]> {
	const params = new URLSearchParams()
	if (filters?.language) params.set('language', filters.language)
	if (filters?.gender) params.set('gender', filters.gender)
	if (filters?.emotion_only) params.set('emotion_only', '1')
	if (filters?.search) params.set('search', filters.search)
	const qs = params.toString()
	const res = await api.request<VoicesResponse>(
		`/api/v1/heygen/voices${qs ? `?${qs}` : ''}`,
		{ method: 'GET' },
	)
	return res.voices || []
}

export async function listHeyGenTranslationLanguages(): Promise<HeyGenLanguage[]> {
	const res = await api.request<LanguagesResponse>('/api/v1/heygen/translation-languages', {
		method: 'GET',
	})
	return res.languages || []
}

export async function getHeyGenConstants(): Promise<HeyGenConstants> {
	const res = await api.request<ConstantsResponse>('/api/v1/heygen/constants', { method: 'GET' })
	return {
		emotions: res.emotions,
		avatar_styles: res.avatar_styles,
		orientations: res.orientations,
		voice_speed: res.voice_speed,
		translate_modes: res.translate_modes,
	}
}

// ── Helpers ──────────────────────────────────────────────────────

/** Определяет к какой HeyGen-модели относится backendModel. */
export function getHeyGenModelKind(
	backendModel?: string | null,
): 'avatar-iv' | 'video-agent' | 'video-translate' | null {
	if (!backendModel) return null
	const m = backendModel.toLowerCase()
	if (m.includes('heygen-avatar') || m.includes('heygen/avatar')) return 'avatar-iv'
	if (m.includes('heygen-video-agent') || m.includes('heygen/video-agent')) return 'video-agent'
	if (m.includes('heygen-video-translate') || m.includes('heygen/video-translate'))
		return 'video-translate'
	return null
}

export function isHeyGenModel(backendModel?: string | null): boolean {
	return getHeyGenModelKind(backendModel) !== null
}
